/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GamoraControllerContextIdentifier,
  GamoraFoundationGlueListenerType,
  GamoraFoundationNodeArgs,
  assert,
  debugCreate,
} from '@ver.id/libs.internal.gamora';
import {
  HoruzProtocolChulakClientStateInvoker,
  HoruzProtocolChulakClientStateInvokerVerifyAppPairingInput,
  HoruzProtocolChulakClientStateYielder,
  HoruzProtocolChulakClientStateYielderCompletedAppPairingInput,
} from './enums.js';
import {
  HoruzProtocolChulakMessageAppPairingEnterGuard,
  HoruzProtocolChulakMessageAppPairingExitGuard,
  HoruzProtocolChulakMessageAppPairingMethod,
  HoruzProtocolChulakMessageAppPresentationEnterGuard,
} from '../../guards/message/index.js';
import { HoruzProtocolChulakClientStateBase } from './base.js';
import { HoruzProtocolChulakClientStateContext } from '../context.js';
import { HoruzProtocolChulakClientStateMap } from '../../statemap.js';
import { HoruzProtocolChulakDataType } from '../../data.js';
import { HoruzProtocolChulakErrorProtocolInvalidMessage } from '../../errors/protocol/invalid-message.error.js';
import { HoruzProtocolChulakMessageAppPairingCompletedGuard } from '../../guards/message/app-pairing-completed.js';
import { HoruzProtocolChulakMessageModule } from '../../modules/message.js';
import { IHoruz } from '../../../../horuz.js';
const debug = debugCreate({
  namespace: 'horuz:protocols:chulak:states:client:app-pairing',
});

/**
 * Client
 *
 * Initialization state
 * @public
 */
export class HoruzProtocolChulakClientStateAppPairing extends HoruzProtocolChulakClientStateBase<
  typeof HoruzProtocolChulakClientStateMap,
  HoruzProtocolChulakClientStateContext,
  IHoruz
> {
  constructor(args: GamoraFoundationNodeArgs) {
    super(args);

    this._onAppPairingCompletedMessage =
      this._onAppPairingCompletedMessage.bind(this);
    this._onAppPresentationEnterMessage =
      this._onAppPresentationEnterMessage.bind(this);
    this._onAppPairingExitMessage = this._onAppPairingExitMessage.bind(this);
  }

  /**
   * Event on message
   * @param contextIdentifier The context identifier
   */
  private _onQueueNext = async (
    clientStateContext: HoruzProtocolChulakClientStateContext,
  ) => {
    return this.$tryPostTask(clientStateContext, async () => {
      if (this.$hasPostedTransition(clientStateContext)) {
        return;
      }

      try {
        // Execute state
        const result = this.recv(clientStateContext);
        if (result.done === false) {
          // Decode message type
          switch (result.message.type) {
            case HoruzProtocolChulakDataType.DATA_APP_PAIRING_COMPLETED: {
              await this._onAppPairingCompletedMessage(
                clientStateContext,
                result.message,
              );
              break;
            }
            case HoruzProtocolChulakDataType.STATE_APP_PAIRING_EXIT: {
              await this._onAppPairingExitMessage(
                clientStateContext,
                result.message,
              );
              break;
            }
            case HoruzProtocolChulakDataType.STATE_APP_PRESENTATION_ENTER: {
              await this._onAppPresentationEnterMessage(
                clientStateContext,
                result.message,
              );
              break;
            }
            default: {
              throw HoruzProtocolChulakErrorProtocolInvalidMessage.Create(
                'Message ' +
                  result.message.type +
                  ' not supported in AppPairing state.',
              );
            }
          }
        }
      } catch (e: any) {
        debug.trace(e?.message, e?.stack);
        this._tryHandleException(clientStateContext, e);
      }
    });
  };

  /**
   * Event
   *
   * Handle
   */
  private async _onAppPresentationEnterMessage(
    clientStateContext: HoruzProtocolChulakClientStateContext,
    message: HoruzProtocolChulakMessageAppPresentationEnterGuard,
  ) {
    this.$postTransition(clientStateContext, { stateNext: 'AppPresentation' });
  }

  /**
   * Send pairing completed
   */
  private async _onAppPairingCompletedMessage(
    clientStateContext: HoruzProtocolChulakClientStateContext,
    input: HoruzProtocolChulakClientStateYielderCompletedAppPairingInput,
  ) {
    // Create pairing completed message
    const appPairingCompleted =
      HoruzProtocolChulakMessageAppPairingCompletedGuard.fromJSON({});

    // Send message
    await this.send(clientStateContext, appPairingCompleted);
  }

  /**
   * Event
   *
   * On pairing exit
   */
  private async _onAppPairingExitMessage(
    clientStateContext: HoruzProtocolChulakClientStateContext,
    message: HoruzProtocolChulakMessageAppPairingExitGuard,
  ) {}

  /**
   * Enter state
   */
  override async $enter(
    clientStateContext: HoruzProtocolChulakClientStateContext,
  ): Promise<void> {
    assert(
      clientStateContext.stateContextRegistry.localServiceSession,
      'Invalid localServiceSession: not defined.',
    );
    try {
      // Listen for messages
      this.$root
        .$('queues')
        .$('data')
        .$on(clientStateContext.contextIdentifier, 'next', () =>
          this._onQueueNext(clientStateContext),
        );

      // Register yield
      this.$registerYield(
        clientStateContext.contextIdentifier,
        HoruzProtocolChulakClientStateYielder.CompletedAppPairing,
        this._onAppPairingCompletedMessage.bind(this),
      );

      // Replay messages in state
      const isNotFresh = await this.replayMessagesInState(clientStateContext, [
        [
          HoruzProtocolChulakDataType.DATA_APP_PAIRING_COMPLETED,
          this._onAppPairingCompletedMessage,
        ],
      ]);

      // Fresh session
      if (!isNotFresh) {
        const message =
          HoruzProtocolChulakMessageModule.Decode<HoruzProtocolChulakMessageAppPairingEnterGuard>(
            clientStateContext.stateContextRegistry.localServiceSession.getDataByType(
              HoruzProtocolChulakDataType.STATE_APP_PAIRING_ENTER,
            ),
          );

        // Work out enter message
        switch (message.method) {
          case HoruzProtocolChulakMessageAppPairingMethod.None: {
            // Complete the pairing immediately
            await this.$executeYielder(
              clientStateContext.contextIdentifier,
              HoruzProtocolChulakClientStateYielder.CompletedAppPairing,
              clientStateContext,
              {},
            );
            break;
          }
          case HoruzProtocolChulakMessageAppPairingMethod.Pin: {
            // Execute invocation
            await this.$executeInvocation<HoruzProtocolChulakClientStateInvoker>(
              clientStateContext.contextIdentifier,
              HoruzProtocolChulakClientStateInvoker.VerifyAppPairing,
              clientStateContext,
              {
                method: 'pin',
                code: message.code,
              },
            );
            break;
          }
        }
      }
    } catch (e: any) {
      debug.trace(e?.message, e?.stack);
      this._tryHandleException(clientStateContext, e);
    }
  }

  /**
   * Exit state
   */
  override async $exit(
    clientStateContext: HoruzProtocolChulakClientStateContext,
  ): Promise<void> {
    this.$unregisterYield(
      clientStateContext.contextIdentifier,
      HoruzProtocolChulakClientStateYielder.CompletedAppPairing,
    );

    // Stop listening for messages
    this.$root
      .$('queues')
      .$('data')
      .$removeEventListenersByNamespaceAndName(
        clientStateContext.contextIdentifier,
        'next',
      );
  }

  /**
   * Register invokers
   */
  invoker(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: typeof HoruzProtocolChulakClientStateInvoker.VerifyAppPairing,
    listener: (
      clientStateContext: HoruzProtocolChulakClientStateContext,
      input: HoruzProtocolChulakClientStateInvokerVerifyAppPairingInput,
    ) => Promise<void>,
  ): this;
  invoker<K extends keyof typeof HoruzProtocolChulakClientStateInvoker>(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: K,
    listener: GamoraFoundationGlueListenerType,
  ): this {
    return super.$registerInvoker(contextIdentifier, name, listener);
  }

  /**
   * Register yielders
   * @public
   */
  yielder(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: typeof HoruzProtocolChulakClientStateYielder.CompletedAppPairing,
    input: HoruzProtocolChulakClientStateYielderCompletedAppPairingInput,
  ): this;
  yielder<K extends keyof typeof HoruzProtocolChulakClientStateYielder>(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: K,
    ...args: unknown[]
  ): this {
    const context = this.$controller.$getContext(contextIdentifier);
    return super.$executeYielder(
      contextIdentifier,
      name,
      context.stateContext,
      ...args,
    );
  }
}
